import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon, ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, MagnifyingGlassIcon,
  PlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  useSaveSurvey, useSubmission, useSubmissions, useSurvey, useSurveyStaff, useSurveys, useSurveyPdf,
} from '../hooks/useCommercialSurveys';
import api from '../services/api';

// Commercial Surveys: the office raises a survey, assigns it to a surveyor, and
// follows it through to the FNO's answer.
//
// The surveyor answers the Survey Outcome form on his phone and the
// Installation Outcome form during or after the install, so this screen is
// about initiating, assigning and reading what came back - never filling the
// forms in on his behalf.

const STATUS_TONE = {
  'Survey Pending': 'bg-amber-50 text-amber-800 border-amber-200',
  'Survey Complete/Installation Pending': 'bg-sky-50 text-sky-700 border-sky-200',
  'Survey Outcome - UTI': 'bg-red-50 text-red-700 border-red-200',
  'Installation Complete': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Unable to Install': 'bg-red-50 text-red-700 border-red-200',
  Cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

const num = (n) => Number(n || 0).toLocaleString('en-GB');
const ukDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { timeZone: 'Europe/London' }) : '—');
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
}).replace(',', '') : '—');
const forInput = (d) => (d ? String(d).slice(0, 10) : '');

const field = 'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const label = 'block text-xs font-semibold uppercase tracking-wide text-gray-500';

function Pill({ status }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  );
}

// Photos come back through the API with the caller's token, so they cannot be
// dropped into an <img src>; each one is fetched once and held as a blob.
function Photo({ fileId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    let made = null;
    api.get(`/api/commercial-surveys/files/${fileId}`, { responseType: 'blob' })
      .then((r) => { if (!live) return; made = URL.createObjectURL(r.data); setUrl(made); })
      .catch(() => {});
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [fileId]);
  if (!url) return <div className="h-24 w-24 animate-pulse rounded-md bg-gray-100" />;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt="Survey evidence" className="h-24 w-24 rounded-md border border-gray-200 object-cover" />
    </a>
  );
}

// Raising a survey is a form to fill in, so it sits in the middle of the screen
// with the list dimmed behind it. Reading back what the surveyor sent is a
// side-by-side job, so that one stays a side panel.
function Modal({ title, subtitle, onClose, children, footer, wide }) {
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div
        className={`flex max-h-full w-full flex-col overflow-hidden rounded-xl bg-gray-50 shadow-2xl ${wide ? 'max-w-5xl' : 'max-w-3xl'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:text-gray-700" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? <div className="border-t border-gray-200 bg-white px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

function Drawer({ title, subtitle, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col bg-gray-50 shadow-2xl" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:text-gray-700" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? <div className="border-t border-gray-200 bg-white px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

// Raise a survey, or correct one. The same fields either way - an edit is the
// same conversation as a creation, a day later.
function StaffPicker({ staff, valueId, valueName, onPick }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const chosen = staff.find((s) => s.entra_user_id === valueId);
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? staff.filter((s) => `${s.display_name} ${s.email || ''}`.toLowerCase().includes(needle))
      : staff;
    return list.slice(0, 30);
  }, [staff, q]);

  return (
    <div className="relative">
      <input
        id="survey-assigned"
        value={open ? q : (chosen?.display_name || valueName || '')}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => { setQ(''); setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder="Type a name to search…"
        className={field}
        autoComplete="off"
      />
      {chosen && !open ? (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onPick(null); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
          aria-label="Clear"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      ) : null}
      {open ? (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {shown.map((s) => (
            <li key={s.entra_user_id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(s); setOpen(false); }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-brand-light"
              >
                <span className="font-medium text-gray-900">{s.display_name}</span>
                {s.email ? <span className="block text-xs text-gray-400">{s.email}</span> : null}
              </button>
            </li>
          ))}
          {!shown.length ? <li className="px-3 py-2 text-sm text-gray-400">Nobody matches.</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

function SurveyForm({ survey, options, onClose }) {
  const staff = useSurveyStaff();
  const save = useSaveSurvey();
  const [f, setF] = useState(() => ({
    work_order_id: survey?.work_order_id || '',
    customer_number: survey?.customer_number || '',
    address: survey?.address || '',
    fno: survey?.fno || '',
    survey_date: forInput(survey?.survey_date),
    installation_date: forInput(survey?.installation_date),
    installation_method: survey?.installation_method || '',
    assigned_entra_id: survey?.assigned_entra_id || '',
    install_assigned_entra_id: survey?.install_assigned_entra_id || '',
    status: survey?.status || 'Survey Pending',
  }));
  // "Other" keeps its own wording: the method column holds the free text, so a
  // one-off method is not lost behind the word Other.
  const known = options.methods.filter((m) => m !== 'Other');
  const [otherMethod, setOtherMethod] = useState(
    survey?.installation_method && !known.includes(survey.installation_method) ? survey.installation_method : '');
  const isOther = f.installation_method === 'Other' || Boolean(otherMethod && !known.includes(f.installation_method));

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    const chosen = staff.data?.find((s) => s.entra_user_id === f.assigned_entra_id);
    const chosenInstall = staff.data?.find((s) => s.entra_user_id === f.install_assigned_entra_id);
    try {
      await save.mutateAsync({
        id: survey?.id,
        ...f,
        installation_method: isOther ? (otherMethod.trim() || 'Other') : f.installation_method,
        assigned_name: chosen ? chosen.display_name : null,
        install_assigned_name: chosenInstall ? chosenInstall.display_name : null,
      });
      onClose();
    } catch (err) {
      window.alert(err?.response?.data?.error || err.message);
    }
  };

  return (
    <Modal
      title={survey ? `Edit ${survey.reference}` : 'New survey'}
      subtitle={survey ? survey.address : 'Raise a commercial survey and assign it to a surveyor'}
      onClose={onClose}
      footer={(
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={save.isPending || !f.address.trim()}
            className="flex-1 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : survey ? 'Save changes' : 'Create survey'}
          </button>
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:border-gray-400">
            Cancel
          </button>
        </div>
      )}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label>
          <span className={label}>1. Work order ID</span>
          <input id="survey-wo" value={f.work_order_id} onChange={set('work_order_id')} className={field} placeholder="WO1454249" />
        </label>
        <label>
          <span className={label}>Customer number</span>
          <input id="survey-customer" value={f.customer_number} onChange={set('customer_number')} className={field} />
        </label>
        <label className="sm:col-span-2">
          <span className={label}>2. Address</span>
          <textarea id="survey-address" value={f.address} onChange={set('address')} rows={2} className={field} />
        </label>
        <label>
          <span className={label}>3. Survey date</span>
          <input id="survey-date" type="date" value={f.survey_date} onChange={set('survey_date')} className={field} />
        </label>
        <div>
          <span className={label}>4. Survey assigned to</span>
          <StaffPicker
            staff={staff.data || []}
            valueId={f.assigned_entra_id}
            valueName={survey?.assigned_name}
            onPick={(p) => setF((prev) => ({ ...prev, assigned_entra_id: p ? p.entra_user_id : '' }))}
          />
        </div>
        {/* The install is often a different person weeks later. Left empty, the
            surveyor keeps both jobs, which is how most of them run. */}
        <div>
          <span className={label}>Installation outcome assigned to</span>
          <StaffPicker
            staff={staff.data || []}
            valueId={f.install_assigned_entra_id}
            valueName={survey?.install_assigned_name}
            onPick={(p) => setF((prev) => ({ ...prev, install_assigned_entra_id: p ? p.entra_user_id : '' }))}
          />
          <span className="mt-1 block text-xs text-gray-500">
            Leave empty if the surveyor is doing the installation paperwork too.
          </span>
        </div>
        <label>
          <span className={label}>5. Installation date</span>
          <input id="survey-install-date" type="date" value={f.installation_date} onChange={set('installation_date')} className={field} />
        </label>
        <label>
          <span className={label}>6. Installation method</span>
          <select
            id="survey-method"
            value={isOther ? 'Other' : f.installation_method}
            onChange={(e) => { setF((p) => ({ ...p, installation_method: e.target.value })); if (e.target.value !== 'Other') setOtherMethod(''); }}
            className={field}
          >
            <option value="">— select method —</option>
            {options.methods.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {isOther ? (
            <input
              id="survey-method-other"
              value={otherMethod}
              onChange={(e) => setOtherMethod(e.target.value)}
              placeholder="Describe the method"
              className={`${field} mt-2`}
            />
          ) : null}
        </label>
        <label>
          <span className={label}>7. FNO</span>
          <select id="survey-fno" value={f.fno} onChange={set('fno')} className={field}>
            <option value="">— select FNO —</option>
            {options.fnos.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
        <label>
          <span className={label}>Status</span>
          <select id="survey-status" value={f.status} onChange={set('status')} className={field}>
            {options.statuses.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </label>
      </div>
      {survey?.events?.length ? (
        <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-700">History</h3>
          <ul className="mt-2 space-y-1 text-xs text-gray-600">
            {survey.events.map((e, i) => (
              <li key={`${e.created_at}-${i}`}>
                <span className="tabular-nums text-gray-400">{ukDateTime(e.created_at)}</span>{' '}
                <span className="font-medium">{e.event.replace(/_/g, ' ')}</span>
                {e.detail ? ` — ${e.detail}` : ''}{e.by_name ? ` (${e.by_name})` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}

// What came back from the field: every answered question in order, with its
// photos. Questions the branching skipped simply are not there.
function SubmissionView({ survey, formKey, onClose }) {
  // A property can be surveyed more than once - reassigned a week later and
  // surveyed again - so every attempt is kept and can be read back here.
  const attempts = useSubmissions(survey.id, formKey);
  const pdf = useSurveyPdf();
  const [chosen, setChosen] = useState(null);
  const { data, isLoading } = useSubmission(survey.id, formKey, chosen);
  const title = formKey === 'survey_outcome' ? 'Survey Outcome' : 'Installation Outcome';
  const submitted = (attempts.data || []).filter((a) => a.status === 'submitted');
  const answers = data?.answers_readable || [];
  const filesByQuestion = useMemo(() => {
    const m = new Map();
    (data?.files || []).forEach((f) => {
      if (!m.has(f.question_id)) m.set(f.question_id, []);
      m.get(f.question_id).push(f);
    });
    return m;
  }, [data]);

  return (
    <Modal wide title={`${title} · ${survey.reference}`} subtitle={survey.address} onClose={onClose}>
      {submitted.length > 1 ? (
        <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Surveyed {submitted.length} times
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {submitted.map((a, i) => {
              const active = (chosen || submitted[0].id) === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setChosen(a.id)}
                  className={`rounded-md border px-3 py-1.5 text-left text-xs ${
                    active ? 'border-brand bg-brand-light text-brand-dark' : 'border-gray-300 bg-white text-gray-600 hover:border-brand'}`}
                >
                  <span className="block font-semibold">
                    {i === 0 ? 'Latest' : `Survey ${submitted.length - i}`} · {ukDate(a.submitted_at)}
                  </span>
                  <span className="block text-gray-400">{a.submitted_by_name || 'unknown'}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      {data?.empty ? <p className="text-sm text-gray-500">This form has not been filled in yet.</p> : null}
      {data && !data.empty ? (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {data.status === 'submitted'
                ? `Submitted ${ukDateTime(data.submitted_at)} by ${data.submitted_by_name || 'unknown'}`
                : `Draft, started ${ukDateTime(data.started_at)}`}
            </p>
            {/* The record as a document, for sending on. A photo-heavy handover
                takes a few seconds to build, hence the pending state. */}
            <button
              type="button"
              disabled={pdf.isPending}
              onClick={() => pdf.mutate({ surveyId: survey.id, formKey })}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-brand hover:text-brand-dark disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {pdf.isPending ? 'Building PDF…' : 'Download PDF'}
            </button>
          </div>
          {pdf.isError ? (
            <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {pdf.error?.response?.data?.error || 'Could not build the PDF.'}
            </p>
          ) : null}
          <div className="space-y-2">
            {answers.map((a) => {
              const files = filesByQuestion.get(a.id) || [];
              return (
                <div key={a.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="text-xs font-medium text-gray-500">{a.question}</p>
                  {a.type === 'photo' || a.type === 'photos' || a.type === 'signature' ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {files.length
                        ? files.map((f) => <Photo key={f.id} fileId={f.id} />)
                        : <span className="text-sm text-gray-400">No image attached</span>}
                    </div>
                  ) : (
                    <p className="mt-0.5 text-sm font-medium text-gray-900">
                      {Array.isArray(a.answer) ? a.answer.join(', ') : String(a.answer ?? '—')}
                    </p>
                  )}
                </div>
              );
            })}
            {!answers.length ? <p className="text-sm text-gray-400">Nothing has been answered yet.</p> : null}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function CommercialSurveys() {
  const [status, setStatus] = useState('');
  const [legacy, setLegacy] = useState(false);
  const [fno, setFno] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // survey object, or 'new'
  const [viewing, setViewing] = useState(null); // { survey, formKey }
  const [expanded, setExpanded] = useState(null);

  const filters = useMemo(() => ({
    status: status || undefined, fno: fno || undefined, q: q || undefined, legacy: legacy ? 1 : undefined,
  }), [status, fno, q, legacy]);
  const { data, isLoading, isFetching, refetch } = useSurveys(filters);
  const detail = useSurvey(editing && editing !== 'new' ? editing : null);

  const statuses = data?.statuses || [];
  const counts = data?.by_status || {};
  const options = { statuses, fnos: data?.fnos || [], methods: data?.methods || [] };

  return (
    <div className="min-w-0 max-w-full p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commercial Surveys</h1>
          <p className="mt-1 text-sm text-gray-500">
            {legacy
              ? 'Surveys carried over from the old NEO system. Their photos are still held there.'
              : 'Raise a survey, assign it to a surveyor, and follow it through to the install.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => refetch()} className="rounded-md border border-gray-300 p-2 text-gray-500 hover:border-brand" title="Refresh">
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          {/* Everything NEO1 held, read-only history rather than work in hand. */}
          <button
            type="button"
            onClick={() => { setLegacy((v) => !v); setStatus(''); }}
            className={`rounded-md border px-3 py-2 text-sm font-medium ${
              legacy ? 'border-brand bg-brand-light text-brand-dark' : 'border-gray-300 bg-white text-gray-600 hover:border-brand'}`}
          >
            {legacy ? 'Back to current' : `Legacy (${num(data?.legacy_total || 0)})`}
          </button>
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark"
          >
            <PlusIcon className="h-4 w-4" /> New survey
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {[['', 'All'], ...statuses.map((s) => [s, s])].map(([key, name]) => (
          <button
            key={key || 'all'}
            type="button"
            onClick={() => setStatus(key)}
            className={`rounded-full border px-3 py-1 text-sm font-medium ${status === key ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-brand'}`}
          >
            {name === 'Survey Complete/Installation Pending' ? 'Survey Complete' : name}
            <span className={status === key ? 'ml-1 text-white/80' : 'ml-1 text-gray-400'}>
              {key ? (counts[key] || 0) : Object.values(counts).reduce((a, b) => a + b, 0)}
            </span>
          </button>
        ))}
        <div className="relative w-full sm:ml-auto sm:w-auto">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            id="survey-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Address, work order or surveyor…"
            className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:w-72"
          />
        </div>
        <select id="survey-fno-filter" value={fno} onChange={(e) => setFno(e.target.value)} className="rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-brand focus:outline-none">
          <option value="">All FNOs</option>
          {(data?.fnos || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>

      <p className="mt-3 text-xs text-gray-400 sm:hidden">Swipe the table sideways to see every column.</p>
      <div className="mt-2 max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-gray-200 bg-white sm:mt-3" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 shadow-[1px_0_0_#e5e7eb]">Survey</th>
              <th className="px-3 py-2">Work order</th>
              <th className="px-3 py-2">FNO</th>
              <th className="px-3 py-2">Address</th>
              <th className="px-3 py-2">Survey date</th>
              <th className="px-3 py-2">Assigned to</th>
              <th className="px-3 py-2">Install date</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Forms</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : (data?.surveys || []).map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-2 font-mono text-xs font-semibold text-gray-900 shadow-[1px_0_0_#e5e7eb]">
                  {s.reference}
                  {s.is_legacy ? <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] font-semibold text-gray-500">NEO1</span> : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-800">{s.work_order_id || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{s.fno || '—'}</td>
                <td className="px-3 py-2 text-gray-700">
                  <button type="button" onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="flex items-start gap-1 text-left hover:text-brand-dark">
                    {expanded === s.id ? <ChevronDownIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" /> : <ChevronRightIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />}
                    <span className={expanded === s.id ? '' : 'line-clamp-1 max-w-[22rem]'}>{s.address}</span>
                  </button>
                  {expanded === s.id && s.customer_number ? (
                    <span className="mt-1 block text-xs text-gray-400">Customer {s.customer_number}</span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">{ukDate(s.survey_date)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-800">
                  {s.assigned_name || <span className="text-amber-700">Unassigned</span>}
                  {s.install_assigned_name && s.install_assigned_name !== s.assigned_name ? (
                    <span className="block text-xs text-gray-400">Install: {s.install_assigned_name}</span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600">{ukDate(s.installation_date)}</td>
                <td className="px-3 py-2"><Pill status={s.status} /></td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setViewing({ survey: s, formKey: 'survey_outcome' })}
                    className={`mr-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${s.survey_done ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-200 text-gray-400'}`}
                  >
                    {s.survey_done ? (s.survey_count > 1 ? `Surveys (${s.survey_count})` : 'Survey') : 'No survey'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewing({ survey: s, formKey: 'installation_outcome' })}
                    className={`mr-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${s.outcome_done ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-gray-200 text-gray-400'}`}
                  >
                    {s.outcome_done ? 'Outcome' : 'No outcome'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(s.id)}
                    className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand-dark"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && !(data?.surveys || []).length ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">
                No surveys yet. Use New survey to raise the first one.
              </td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {data?.total ? (
        <p className="mt-2 text-xs text-gray-400">
          Showing {data.surveys.length} of {data.total} surveys.
        </p>
      ) : null}

      {editing === 'new' ? <SurveyForm options={options} onClose={() => setEditing(null)} /> : null}
      {editing && editing !== 'new' && detail.data
        ? <SurveyForm survey={detail.data} options={options} onClose={() => setEditing(null)} /> : null}
      {viewing ? <SubmissionView survey={viewing.survey} formKey={viewing.formKey} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

export default CommercialSurveys;
